import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TERMS_TYPE } from './constants/agree';
import TermsModal from './components/TermsModal';
import useCustomHistory from './hooks/useCustomHistory';

type Props = {
  setIsTermShow: (isShow: boolean) => void;
  setIsPrivacyPolicyShow: (isShow: boolean) => void;
  setIsPrivacyShow: (isShow: boolean) => void;
};
const TermsContext = createContext<Props>({
  setIsTermShow: () => {
    return;
  },
  setIsPrivacyPolicyShow: () => {
    return;
  },
  setIsPrivacyShow: () => {
    return;
  },
});

const TERMS_MODAL: [TERMS_TYPE, string][] = [
  [TERMS_TYPE.TERM, 'footer/terms_title'],
  [TERMS_TYPE.PRIVACY_AGREEMENT, 'account/agree/privacy_policy_title'],
  [TERMS_TYPE.PRIVACY_POLICY, 'footer/privacy_policy_title'],
];

export const TermsContextProvider = ({ children }: { children: JSX.Element }): JSX.Element => {
  const { t } = useTranslation();
  const { replace, location } = useCustomHistory();
  const [isTermsShow, setIsTermsShow] = useState<Record<TERMS_TYPE, boolean>>({
    [TERMS_TYPE.TERM]: false,
    [TERMS_TYPE.PRIVACY_POLICY]: false,
    [TERMS_TYPE.PRIVACY_AGREEMENT]: false,
  });

  const setIsShow = useCallback(
    (type: TERMS_TYPE, isShow) => {
      if (!isShow) {
        replace(location.pathname);
      }
      setIsTermsShow({
        ...isTermsShow,
        [type]: isShow,
      });
    },
    [isTermsShow],
  );

  useEffect(() => {
    if (!location.hash || !TERMS_TYPE[location.hash.replace('#', '')]) {
      return;
    }
    setIsShow(TERMS_TYPE[location.hash.replace('#', '')], true);
  }, [location]);

  return (
    <TermsContext.Provider
      value={{
        setIsTermShow: setIsShow.bind(this, TERMS_TYPE.TERM),
        setIsPrivacyPolicyShow: setIsShow.bind(this, TERMS_TYPE.PRIVACY_POLICY),
        setIsPrivacyShow: setIsShow.bind(this, TERMS_TYPE.PRIVACY_AGREEMENT),
      }}
    >
      {children}
      {TERMS_MODAL.map(
        ([type, title]) =>
          isTermsShow[type] && <TermsModal key={type} title={t(title)} type={type} setter={(isShow) => setIsShow(type, isShow)} />,
      )}
    </TermsContext.Provider>
  );
};

export const useTerms = (): Props => useContext(TermsContext);
